import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, mensagemDoErro } from '../api';
import { coordenada, data, numero, paraNumero, plural } from '../formato';
import {
  criarPonto, situacaoDoPonto, verPontosDaOs, type Ponto,
} from '../api/campo';
import { verDiariosDaOs, type Diario } from '../api/diarios';
import { codigoDaCampanha, listarCampanhas, type CampanhaDaLista } from '../api/cptu';
import { FormNovoDiario } from './Diarios';
import { useSessao } from '../sessao';
import {
  Aviso, Campo, Carregando, Dialogo, ErroDaApi, Escolha, Selo, useEstreito,
} from '../ui';
import type { Tom } from '../ui';
import type { Os } from './OsForms';
import { JanelaDeNovaCampanha } from './cptu/Campanhas';
import { SeloDaCampanha } from './cptu/CampanhaMoldura';
import { SondagensSptDaOs } from './spt/SondagensSptDaOs';
import { ReferenciaNivelDaOs } from './ReferenciaNivelDaOs';
import { LocacaoNoMapa } from './LocacaoNoMapa';
import { contarPontos, nomeDoPonto } from '../nomeDoPonto';

/**
 * O campo desta OS: furos, verticais e pontos, e os diários.
 *
 * ── Por que uma tela própria (onda 204) ──────────────────────────────
 *
 * «A parte de campo poderia ser suprimida dessa seção de OS, pois deverá
 *  ser um módulo à parte» — Humberto, 22/09/2026. Os dois blocos abaixo
 * moravam no meio da ficha da OS, entre a programação de amostras e os
 * quantitativos, e a ficha rolava por três telas antes de chegar em
 * «Dados da OS». Saem de lá; mas não somem — o furo continua pendendo da
 * ORDEM DE SERVIÇO (o SP-01 de 2024 não é o SP-01 de 2026), e é a OS
 * que dá o caminho até aqui. Quando o módulo de campo (diário, fotos,
 * geolocalização) ficar de pé, ele assume esta porta; até lá, ela é a
 * mesma funcionalidade, só que fora do caminho de quem programa ensaios.
 */
export function OsCampo() {
  const { id = '' } = useParams();
  const { pode, temEspaco } = useSessao();
  const [os, setOs] = useState<Os | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    let vivo = true;
    api<Os>(`/os/${id}`).then((o) => { if (vivo) setOs(o); })
      .catch((e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, [id]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!os) return <Carregando texto="Carregando a ordem de serviço" />;

  const cancelada = Boolean(os.cancelada);
  /* Furos e diários são do Campo (H1, onda 295): quem os lança é quem tem o
     «Editar» do Campo — o sondador, que da OS só tem o «Ver» (D6). */
  const podeEditar = pode('campo.editar') && !cancelada;
  /* A ficha da OS é do Hub e do LabManager: para quem só tem o Campo, o link
     levaria a uma recusa (spec 03 §10). */
  const temFicha = temEspaco('hub') || temEspaco('labmanager');

  return (
    <div>
      <div className="cabeca">
        <div className="linha-topo">
          <h1 className="cresce">
            Campo · <span className="mono">{os.numero}</span>
          </h1>
          {temFicha && (
            <Link className="botao secundario" to={`/os/${id}`}>← Ficha da OS</Link>
          )}
        </div>
        <p className="sub">{os.obra_nome} · {os.cliente}</p>
      </div>

      {cancelada && (
        <Aviso tipo="atencao" titulo="Esta OS está cancelada.">
          Os pontos, as campanhas e os diários ficam como registro; nada novo entra aqui.
        </Aviso>
      )}

      <PontosDaOs osId={id} podeEditar={podeEditar} />
      <ReferenciaNivelDaOs osId={id} podeEditar={podeEditar} />
      {pode('cptu.ver') && (
        <CampanhasDaOs os={os} podeCriar={pode('cptu.editar') && !cancelada} />
      )}
      {pode('spt.ver') && (
        <SondagensSptDaOs os={{ id: os.id, numero: String(os.numero) }} podeCriar={pode('spt.editar') && !cancelada} />
      )}
      <DiariosDaOs osId={id} podeEditar={podeEditar} />
    </div>
  );
}

/**
 * Os furos, verticais e pontos desta OS.
 *
 * ── Por que aqui e não no menu ──────────────────────────────────────
 *
 * Porque o ponto pende da ORDEM DE SERVIÇO. A mesma obra pode ter
 * campanhas em anos diferentes, e o SP-01 de 2024 não é o SP-01 de 2026
 * — quem procura um furo está olhando uma OS, não um cadastro geral.
 *
 * ── O que este bloco NÃO faz ────────────────────────────────────────
 *
 * Não mede. Metro perfurado, ponto e vertical são critérios de medição
 * de campo que a Testesolo ainda não definiu; o que se guarda aqui é a
 * profundidade PREVISTA. A executada virá do boletim de campo.
 *
 * E não apaga: furo que não vai ser feito é `cancelado` com motivo. Furo
 * que some da lista é metro que ninguém explica na medição.
 */
function PontosDaOs({ osId, podeEditar }: {
  osId: string; podeEditar: boolean;
}) {
  const [linhas, setLinhas] = useState<Ponto[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [novo, setNovo] = useState(false);
  const [mudando, setMudando] = useState<Ponto | null>(null);
  /* 371: a locação pelo mapa. */
  const [locando, setLocando] = useState(false);

  const carregar = useCallback(async () => {
    setErro(null);
    try { setLinhas(await verPontosDaOs(osId)); } catch (e) { setErro(e); }
  }, [osId]);

  useEffect(() => { void carregar(); }, [carregar]);

  const parado = (p: Ponto) => ['suspenso', 'cancelado'].includes(p.situacao);

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Furos, verticais e pontos</h2>
        <span className="sub cresce">
          {linhas == null ? 'carregando'
            : linhas.length === 0 ? 'nenhum ponto cadastrado'
              : contarPontos(linhas.map((p) => p.tipo))}
        </span>
        {podeEditar && linhas != null && linhas.length > 0 && (
          <button type="button" className="botao secundario locar-no-mapa"
            onClick={() => setLocando(true)}>
            Locar no mapa
          </button>
        )}
        {podeEditar && (
          <button type="button" className="botao secundario"
            onClick={() => setNovo(true)}>
            Novo ponto
          </button>
        )}
      </div>

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : linhas.length === 0 ? (
          <p className="sub">
            Ainda não há furo, vertical ou ponto cadastrado nesta OS.
            Enquanto não houver, a amostra continua identificando o furo por
            texto livre — e «SP-01», «SP 01» e «sp-01» contam como três furos
            na hora de somar metro. A vertical CPTu entra sozinha, pela
            importação da campanha.
          </p>
        ) : (
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Ponto</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Prevista</th>
                  <th scope="col" className="col-opcional">Coordenada</th>
                  <th scope="col">Rendeu</th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((p) => (
                  <tr key={p.id} className={parado(p) ? 'desc' : undefined}>
                    <th scope="row">
                      <strong>{p.codigo}</strong>
                      {p.descricao && <span className="sub">{p.descricao}</span>}
                    </th>
                    <td>{TIPO_PONTO[p.tipo] ?? p.tipo}</td>
                    <td className="n">
                      {p.profundidade_prevista
                        ? `${numero(Number(p.profundidade_prevista), 2)} m`
                        : <span className="nada">—</span>}
                    </td>
                    <td className="col-opcional mono">
                      {/* O formatador da casa, o mesmo da ficha da vertical CPTu (agent-browser da 312). */}
                      {coordenada(p.latitude, p.longitude)
                        || <span className="nada">sem coordenada</span>}
                      {p.coordenada_pelo_mapa && p.latitude != null && (
                        <span className="sub" title="Locada no mapa — não levantada em campo"> (estimada)</span>
                      )}
                    </td>
                    <td>
                      {p.amostras > 0
                        ? `${p.amostras} amostra(s)${
                          p.ensaios > 0 ? ` · ${p.ensaios} ensaio(s)` : ''}`
                        : <span className="nada">nada ainda</span>}
                    </td>
                    <td>
                      <Selo tom={TOM_PONTO[p.situacao] ?? 'neutro'}>
                        {SITUACAO_PONTO[p.situacao] ?? p.situacao}
                      </Selo>
                      {p.motivo_situacao && (
                        <span className="sub">{p.motivo_situacao}</span>
                      )}
                      {podeEditar && (
                        <button type="button" className="ligacao"
                          onClick={() => setMudando(p)}>
                          mudar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {locando && linhas && (
        <LocacaoNoMapa osId={osId} pontos={linhas} nomeDoTipo={(t) => TIPO_PONTO[t] ?? t}
          aoFechar={() => setLocando(false)} aoGravado={carregar} />
      )}
      {novo && (
        <FormPonto osId={osId} aoFechar={() => setNovo(false)}
          aoPronto={() => { setNovo(false); void carregar(); }} />
      )}
      {mudando && (
        <DialogoSituacaoPonto ponto={mudando}
          aoFechar={() => setMudando(null)}
          aoPronto={() => { setMudando(null); void carregar(); }} />
      )}
    </div>
  );
}

/**
 * As campanhas CPTu desta OS — onda 312 (Tarefa 312.5).
 *
 * A campanha nasce da OS, e é aqui, no Campo da OS, que quem programa o dia
 * de campo a encontra: a lista (com a situação e as verticais) e «＋ Nova
 * campanha». A importação e o relatório moram na casca da campanha (`/cptu/:id`).
 * No celular, só consulta: a campanha nova e a importação são no computador.
 */
function CampanhasDaOs({ os, podeCriar }: { os: Os; podeCriar: boolean }) {
  const estreito = useEstreito();
  const [linhas, setLinhas] = useState<CampanhaDaLista[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [nova, setNova] = useState(false);

  useEffect(() => {
    let vivo = true;
    listarCampanhas({ os: os.id })
      .then((r) => { if (vivo) setLinhas(r); })
      .catch((e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, [os.id]);

  return (
    <div className="cartao campanhas-da-os">
      <div className="cartao-cabeca">
        <h2>Campanhas CPTu desta OS</h2>
        <span className="sub cresce">
          {linhas == null ? 'carregando'
            : linhas.length === 0 ? 'nenhuma campanha'
              : plural(linhas.length, 'campanha', 'campanhas')}
        </span>
        {podeCriar && !estreito && (
          <button type="button" className="botao secundario" onClick={() => setNova(true)}>
            ＋ Nova campanha
          </button>
        )}
      </div>
      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando texto="Carregando as campanhas da OS" />
        : linhas.length === 0 ? (
          <p className="sub">
            Nenhuma campanha de piezocone nesta OS. A campanha reúne as verticais
            CPTu — cada uma liga-se a um ponto da OS na importação dos arquivos
            do equipamento.
          </p>
        ) : estreito ? (
          /* No celular, a linha inteira é o alvo (≥ 44 px), como os cartões da lista `/cptu` (agent-browser da 312). */
          <ul className="cptu-cartoes" aria-label="Campanhas CPTu desta OS">
            {linhas.map((c) => (
              <li key={c.id}>
                <Link className="cptu-cartao" to={`/cptu/${c.id}`}>
                  <span className="cptu-cartao-topo">
                    <b className="mono">{codigoDaCampanha(c.sequencia)}</b>
                    <SeloDaCampanha situacao={c.situacao} />
                  </span>
                  <span className="cptu-cartao-titulo">{c.titulo}</span>
                  <span className="sub">
                    {plural(c.verticais, 'vertical', 'verticais')}
                    {c.verticais > 0 && ` · ${numero(c.metragem, 1)} m`}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Campanha</th>
                  <th scope="col" className="n">Verticais</th>
                  <th scope="col" className="n col-opcional">Metragem</th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((c) => (
                  <tr key={c.id}>
                    <th scope="row">
                      <Link to={`/cptu/${c.id}`} className="mono">{codigoDaCampanha(c.sequencia)}</Link>
                      <span className="sub">{c.titulo}</span>
                    </th>
                    <td className="n mono">{c.verticais}</td>
                    <td className="n mono col-opcional">
                      {c.verticais ? `${numero(c.metragem, 1)} m` : <span className="nada">—</span>}
                    </td>
                    <td><SeloDaCampanha situacao={c.situacao} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {nova && (
        <JanelaDeNovaCampanha os={{ id: os.id, numero: String(os.numero) }} aoFechar={() => setNova(false)} />
      )}
    </div>
  );
}

/**
 * Os dias de campo desta OS.
 *
 * A agenda geral («o que as equipes fizeram ontem?») fica em Campo. Aqui
 * é a pergunta do outro lado: «o que já foi feito NESTA obra?» — e é
 * daqui que um dia novo nasce, porque é aqui que estão a obra e os furos.
 */
function DiariosDaOs({ osId, podeEditar }: {
  osId: string; podeEditar: boolean;
}) {
  const [linhas, setLinhas] = useState<Diario[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [abrindo, setAbrindo] = useState(false);
  const navegar = useNavigate();

  const carregar = useCallback(async () => {
    setErro(null);
    try { setLinhas(await verDiariosDaOs(osId)); } catch (e) { setErro(e); }
  }, [osId]);

  useEffect(() => { void carregar(); }, [carregar]);

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Diários de campo</h2>
        <span className="sub cresce">
          {linhas == null ? 'carregando'
            : linhas.length === 0 ? 'nenhum dia registrado'
              : `${linhas.length} dia(s)`}
        </span>
        {podeEditar && (
          <button type="button" className="botao secundario"
            onClick={() => setAbrindo(true)}>
            Abrir diário
          </button>
        )}
      </div>

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : linhas.length === 0 ? (
          <p className="sub">
            Nenhum dia de campo lançado nesta OS. O diário registra a
            jornada, o tempo, as paradas e o avanço de cada furo ou vertical — e é ele
            que responde, depois, por que a semana rendeu o que rendeu.
          </p>
        ) : (
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Dia</th>
                  <th scope="col">Equipe</th>
                  <th scope="col" className="col-opcional">Jornada</th>
                  <th scope="col">Avanço</th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={l.id}>
                    <th scope="row">
                      <Link to={`/campo/diario/${l.id}`}>{data(l.data)}</Link>
                    </th>
                    <td>
                      {l.equipe_codigo ?? <span className="nada">—</span>}
                      {l.veiculo_placa
                        && <span className="sub mono">{l.veiculo_placa}</span>}
                    </td>
                    <td className="col-opcional">
                      {String(l.hora_inicio ?? '').slice(0, 5) || '—'}
                      {'–'}
                      {String(l.hora_fim ?? '').slice(0, 5) || '—'}
                    </td>
                    <td className="n">
                      {l.avanco_m == null
                        ? <span className="nada">—</span>
                        : `${numero(Number(l.avanco_m), 2)} m`}
                    </td>
                    <td>
                      <Selo tom={TOM_DIARIO[l.situacao] ?? 'neutro'}>
                        {SITUACAO_DIARIO[l.situacao] ?? l.situacao}
                      </Selo>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {abrindo && (
        <FormNovoDiario osId={osId} aoFechar={() => setAbrindo(false)}
          aoPronto={(novo) => navegar(`/campo/diario/${novo}`)} />
      )}
    </div>
  );
}

const TOM_DIARIO: Record<string, Tom> = {
  rascunho: 'andamento', fechado: 'ok', cancelado: 'critico',
};

const SITUACAO_DIARIO: Record<string, string> = {
  rascunho: 'Rascunho', fechado: 'Fechado', cancelado: 'Cancelado',
};

const TIPO_PONTO: Record<string, string> = {
  spt: 'SPT', trado: 'Trado', rotativa: 'Rotativa', mista: 'Mista',
  cpt: 'CPT', cptu: 'CPTu', scptu: 'SCPTu', dmt: 'DMT', sdmt: 'SDMT',
  vane: 'Vane', permeabilidade: 'Permeabilidade', placa: 'Placa',
  poco: 'Poço', instrumento: 'Instrumento', outro: 'Outro',
};

const SITUACAO_PONTO: Record<string, string> = {
  previsto: 'Previsto', programado: 'Programado', em_execucao: 'Em execução',
  executado: 'Executado', suspenso: 'Suspenso', cancelado: 'Cancelado',
};

const TOM_PONTO: Record<string, Tom> = {
  previsto: 'neutro', programado: 'neutro', em_execucao: 'andamento',
  executado: 'ok', suspenso: 'atencao', cancelado: 'critico',
};

function FormPonto({ osId, aoFechar, aoPronto }: {
  osId: string; aoFechar(): void; aoPronto(): void;
}) {
  const [codigo, setCodigo] = useState('');
  const [tipo, setTipo] = useState('spt');
  const [prof, setProf] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      await criarPonto(osId, {
        codigo: codigo.trim(), tipo,
        profundidadePrevista: paraNumero(prof) ?? undefined,
        latitude: paraNumero(lat) ?? undefined,
        longitude: paraNumero(lon) ?? undefined,
      });
      aoPronto();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Novo ponto de investigação" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <Campo rotulo="Código" value={codigo}
        dica={`SP-01, CPTU-03, PZ-2 — é como ${tipo && nomeDoPonto(tipo) === 'vertical' ? 'a vertical' : `o ${nomeDoPonto(tipo)}`} aparece no boletim e na medição.`}
        onChange={(ev) => setCodigo(ev.target.value)} />
      <Escolha rotulo="Tipo" valor={tipo} limpavel={false}
        aoEscolher={(v) => setTipo(v ?? 'spt')}
        opcoes={Object.entries(TIPO_PONTO).map(([valor, texto]) => ({
          valor, texto }))} />
      <Campo rotulo="Profundidade prevista (m)" value={prof} inputMode="decimal"
        onChange={(ev) => setProf(ev.target.value)} />
      <div className="linha-campos">
        <Campo rotulo="Latitude" value={lat} inputMode="decimal"
          dica="Graus decimais, como -26,9194"
          onChange={(ev) => setLat(ev.target.value)} />
        <Campo rotulo="Longitude" value={lon} inputMode="decimal"
          onChange={(ev) => setLon(ev.target.value)} />
      </div>
      <p className="sub">
        A coordenada vem inteira ou não vem: meia coordenada não localiza
        nada e ainda parece localizar.
      </p>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={ocupado || codigo.trim().length < 1}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Gravando…' : 'Cadastrar'}
        </button>
      </div>
    </Dialogo>
  );
}

function DialogoSituacaoPonto({ ponto, aoFechar, aoPronto }: {
  ponto: Ponto; aoFechar(): void; aoPronto(): void;
}) {
  const [situacao, setSituacao] = useState(ponto.situacao);
  const [motivo, setMotivo] = useState(ponto.motivo_situacao ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const exigeMotivo = ['suspenso', 'cancelado'].includes(situacao);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      await situacaoDoPonto(ponto.id, {
        situacao, motivo: motivo.trim() || undefined });
      aoPronto();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo={`Situação de ${ponto.codigo}`} aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <Escolha rotulo="Situação" valor={situacao} limpavel={false}
        aoEscolher={(v) => setSituacao(v ?? ponto.situacao)}
        opcoes={Object.entries(SITUACAO_PONTO).map(([valor, texto]) => ({
          valor, texto }))} />
      <label className="campo">
        <span className="rotulo">
          Motivo{exigeMotivo ? '' : ' (opcional)'}
        </span>
        <textarea rows={3} value={motivo}
          onChange={(ev) => setMotivo(ev.target.value)}
          placeholder="sem acesso, sonda quebrada, cliente mandou parar…" />
      </label>
      {exigeMotivo && (
        <p className="sub">
          Metro não executado <b>com</b> motivo é uma conversa; sem motivo
          é uma pendência que ninguém consegue fechar na medição.
        </p>
      )}
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={ocupado || (exigeMotivo && motivo.trim().length < 5)}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Gravando…' : 'Gravar'}
        </button>
      </div>
    </Dialogo>
  );
}

